// externals
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { nanoid } from 'nanoid';
import { RSAA } from 'redux-api-middleware';

// libraries
import type { PendingApplyProgram, ProgramIncentives, ProgramQuote, RetainedProgram } from '@makemydeal/dr-dash-types';
import { incentiveSelectors, paymentPayloadUtils, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import { creditSelectors } from '@makemydeal/dr-platform-shared';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { CalculationEndpointType, ILenderProgram, IResponsePayment, Offer, OfferType } from '@makemydeal/dr-platform-types';
import { ApplicationCodes, CASH } from '@makemydeal/dr-platform-types';
import { TRACE_ID_HEADER } from '@makemydeal/dr-shared-bff-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

// actions
import {
    PROGRAMS_CLEAR_ALL,
    PROGRAMS_SET_CREDIT_SCORE_OVERRIDE,
    PROGRAMS_CLEAR_INCENTIVES,
    PROGRAMS_CLEAR_SELECTED,
    PROGRAMS_INCENTIVES_REQUEST_SUCCESS,
    PROGRAMS_SELECT,
    REQUEST_PROGRAMS,
    REQUEST_PROGRAMS_FAILURE,
    REQUEST_PROGRAMS_SUCCESS,
    TOGGLE_INCENTIVE_FOR_PROGRAM,
    SET_RETAINED_PROGRAM_DISPLAY,
    CLEAR_RETAINED_PROGRAM_DISPLAY,
    SET_APPLIED_PAYMENT_BASELINE,
    CLEAR_APPLIED_PAYMENT_BASELINE,
    SET_PENDING_APPLY_PROGRAM,
    CLEAR_PENDING_APPLY_PROGRAM,
    SET_ALL_TIERS_LOADED,
    ALL_PROGRAMS_LOADED,
    DISABLE_PROGRAM_LOSS_INCENTIVES,
    CLEAR_PROGRAM_LOSS_INCENTIVES,
    PRUNE_PROGRAM_LOSS_INCENTIVES,
    MARK_PROGRAM_AUTO_SELECTED,
    CLEAR_PROGRAM_AUTO_SELECTED
} from '../actionTypes/offerActionTypes';
import {
    OPEN_PROGRAMS_SLIDEOUT,
    CLOSE_PROGRAMS_SLIDEOUT,
    SET_PROGRAMS_CALC_ENDPOINT_STASH
} from '../actionTypes/deskingGlobalActionTypes';

import * as programSelectors from '../selectors/programsSelectors';
import { getSelectedProgram } from '../selectors/programsSelectors';
import { getResolvedAutoSelectChecked } from '../selectors/manualFieldOverridesSelectors';
import {
    getProgramsSlideOutPreviousCalcEndpoint,
    getProgramsSlideOutPreviousOfferType,
    isProgramsSlideOutOpen
} from '../selectors/deskingGlobalSelectors';

// utils

import { getProgramOrchestratorUrl } from '../utils/paymentServiceUtils';
import {
    getCalculationEndpointByOfferType,
    getCurrentOfferType,
    getDealerOptedOutOfAutoSelectProgram,
    getDownPaymentByOfferType,
    getManualCreditScore,
    getManualIncentives,
    getIncentives,
    getSelectedIncentivesByOfferType,
    getSelectedConditionalIdsFromPenciledInputs,
    getSelectedNonConditionalIdsFromPenciledInputs,
    hasRecordedNonConditionalSelection,
    hasRecordedConditionalSelection,
    getSelectedTerm,
    getSelectedTermMonths,
    getShopperZip,
    getTermId,
    getTermIdForOfferType,
    isFlexBuyPayment
} from '../selectors/offerRedux';
import {
    applyDealerOverrides,
    modifyManualIncentive,
    selectedConditionalIncentives,
    updateCurrentCalculationEndpoint
} from './offerRedux';
import {
    EXPLICIT_LENDER_CARRIED_OVERRIDE_FIELDS,
    PROGRAM_RESET_OVERRIDE_FIELDS,
    clearManualFieldOverrides
} from './manualFieldOverridesActionCreators';
import { getFrozenLenderFeeOverrides, resetProgramSuppliedOverrides } from './programOrchestrationActionCreators';
import { CALC_ENDPOINTS } from '../constants';
import * as programPayloadUtil from '../utils/programUtils';
import {
    buildProgramContextId,
    buildProgramContextIdFromTerm,
    isProgramNotExpired,
    mapPaymentResponseToProgramQuote
} from '../utils/programUtils';
import { buildPaymentPayloadHostSelectors } from '../utils/offerPayloadUtil';
import type { GetAllQuotesOptions } from '../types/types';
import type { KeptLender } from '../programInvalidatingActions';
import type { DashStoreThunk } from '../types/appState';

const isRestorationEnabled = (state: any): boolean => featureToggleSelectors.isProgramIncentivesRestorationEnabled(state);

// Gates every DE759607/US2076398 cash-deal-incentives fix in this file (and, via action.meta, in
// programsReducer.ts, which has no direct state access of its own).
const isCashFixesEnabled = (state: any): boolean => offerReduxUtils.incentives.isCashDealIncentivesFixesEnabled(state);

const buildIncentivesContextMeta = (state: any, term: any) => ({
    programContextId: buildProgramContextIdFromTerm(term),
    incentivesInputsKey: programSelectors.getIncentivesInputsFingerprint(state)
});

const isSelectedProgramContextMatch = (selectedProgram: ProgramQuote | undefined, program: ProgramQuote): boolean => {
    if (!selectedProgram) return false;

    return buildProgramContextId(selectedProgram) === buildProgramContextId(program);
};

const buildPendingApplyProgram = (
    program: ProgramQuote,
    offerType: OfferType,
    requestId: string,
    carried: Pick<
        PendingApplyProgram,
        | 'explicitConditionalIds'
        | 'explicitNonConditionalIds'
        | 'deferredStandardsReapplied'
        | 'standardsSelectionEstablished'
        | 'manualProgramSelect'
        | 'reapplyCurrentProgram'
    >
): PendingApplyProgram => ({
    program,
    offerType,
    requestId,
    programContextId: buildProgramContextId(program),
    lenderId: program.lenderProgram?.lenderId,
    term: program.term,
    ...carried
});

export const selectProgramQuote = (record: ProgramQuote, requestId?: string) => {
    return (dispatch: any, getState: any) => {
        const programContextId = buildProgramContextId(record);
        dispatch({
            type: PROGRAMS_SELECT,
            payload: record,
            meta: {
                incentives: true,
                enableProgramIncentivesRestoration: isRestorationEnabled(getState()),
                ...(requestId && { requestId }),
                ...(programContextId && { programContextId })
            }
        });
    };
};

export const toggleIncentiveCheckbox = (incentive: any, selected: boolean) => {
    return {
        type: TOGGLE_INCENTIVE_FOR_PROGRAM,
        payload: { incentive, selected },
        meta: { middleware: { payment: false } }
    };
};

export const modifyProgramIncentive = (incentive: any, selected: boolean, payment: boolean = false) => {
    const modifyAction = selected ? 'add' : 'remove';
    const manualIncentivePayload = {
        ...incentive,
        code: incentive.incentiveId,
        manual: false,
        amount: incentive.cash,
        name: incentive.programName
    };

    return modifyManualIncentive(manualIncentivePayload, modifyAction, payment);
};

export const setProgramsCalcEndpointStash = (previousCalcEndpoint: CalculationEndpointType, previousOfferType: OfferType) => ({
    type: SET_PROGRAMS_CALC_ENDPOINT_STASH,
    payload: { previousCalcEndpoint, previousOfferType }
});

export const forceCalcEndpointToPq = (dispatch: any, state: any, offerType: OfferType) => {
    if (!isRestorationEnabled(state)) return;
    if (getCalculationEndpointByOfferType(state, offerType) !== CALC_ENDPOINTS.userProgramQuotes) return;
    dispatch(setProgramsCalcEndpointStash(CALC_ENDPOINTS.userProgramQuotes, offerType));
    dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, offerType));
};

/**
 * Read directly off `state.featureToggles`, not `featureToggleSelectors.getFeatureToggle` - this
 * predicate is on the hot path of every endpoint-moving primitive in the file, most of them
 * exercised by tests that never bootstrap `sharedBootstrap`, and `getFeatureToggle` throws when it
 * is not.
 */
const dealerOptOutBlocksIncentiveExcursion = (state: any, offerType: OfferType): boolean =>
    state.featureToggles?.DE773081_Enable_Auto_Select_Guard_Fix
        ? getDealerOptedOutOfAutoSelectProgram(state) && !getResolvedAutoSelectChecked(state, offerType)
        : getDealerOptedOutOfAutoSelectProgram(state);

/**
 * An incentive edit must not move a dealer who opted out of Auto Select Program onto the
 * program-quotes engine: that engine answers with a lender program, which lands on every term and
 * is then latched by `syncRetainedProgramDisplay` and held as a penciled override by
 * `setProgramFallbackOverrides` on flyout close — a program and rate the dealer never chose
 * (DE771180). `autoSelectProgramGuardMiddleware` looks like it would stop this but is registered
 * nowhere on purpose, so each endpoint-moving primitive pre-empts on the dealer's setting itself.
 *
 * Reads `getDealerOptedOutOfAutoSelectProgram`, not `getDealerForcedUpq`: the latter goes false once
 * `autoSelectProgramSavedAt` is latched, and that marker is stamped on every MV save, so a
 * marker-sensitive guard would evaporate as soon as the deal is saved once and the defect would
 * return.
 */
const forceCalcEndpointToPqForIncentiveEdit = (dispatch: any, state: any, offerType: OfferType) => {
    if (programSelectors.explicitLenderP1AppliesToOffer(state, offerType)) return;
    if (dealerOptOutBlocksIncentiveExcursion(state, offerType)) return;
    forceCalcEndpointToPq(dispatch, state, offerType);
};

/**
 * DE773956: unlike `dealerOptOutBlocksIncentiveExcursion` above, this is inert with the toggle off -
 * no unconditional fallback - because the callers outside this file (`changeVehicleMiddleware.ts`)
 * are Lease/Balloon-reaching and untestable against a forced-UPQ dealer on any QA rooftop, so the
 * new guard at those sites must not activate before the toggle does.
 *
 * Excludes CASH for the same reason `isOnUpqWithDealerOptOutBlocking` below does:
 * `getResolvedAutoSelectChecked(state, CASH)` is unconditionally `false` (CASH is absent from
 * `autoSelectStateMiddleware`'s `SEEDED_OFFER_TYPES`), so without this exclusion a CASH deal would
 * always read as the excursion being blocked whenever the dealer opted out, regardless of any real
 * per-deal signal - overriding DE759607/US2076398's own cash-incentive protection.
 */
export const dealerOptOutBlocksPqExcursion = (state: any, offerType: OfferType): boolean =>
    offerType !== CASH &&
    Boolean(state.featureToggles?.DE773956_Enable_Auto_Select_Guard_Extension) &&
    getDealerOptedOutOfAutoSelectProgram(state) &&
    !getResolvedAutoSelectChecked(state, offerType);

const shouldPriceOnPqWithoutMovingEndpoint = (state: any, offerType: OfferType): boolean =>
    programSelectors.explicitLenderP1AppliesToOffer(state, offerType) &&
    getCalculationEndpointByOfferType(state, offerType) === CALC_ENDPOINTS.userProgramQuotes;

/**
 * `getResolvedAutoSelectChecked(state, CASH)` is unconditionally false - CASH is absent from
 * `autoSelectStateMiddleware`'s `SEEDED_OFFER_TYPES` - so without this exclusion cash would always
 * read as "dealer opted out" regardless of any real per-deal signal, overriding
 * DE759607/US2076398's own cash-incentive protection.
 */
const isOnUpqWithDealerOptOutBlocking = (state: any, offerType: OfferType): boolean =>
    offerType !== CASH &&
    dealerOptOutBlocksIncentiveExcursion(state, offerType) &&
    getCalculationEndpointByOfferType(state, offerType) === CALC_ENDPOINTS.userProgramQuotes;

/**
 * `forceUseGenericCalcs` is a payment-grid signal - stamping it also arms `getPaymentGridOverridesByTerm`'s
 * grid-only fallbacks (`forcedFallbackSellRate`, `applyGridColumnResiduals`) for a call that never came from
 * the grid. `keepUpqForSuppressedExcursion` gets the same "stay on UPQ" effect from
 * `paymentPayloadUtils.ts`'s `useGenericCalcs`/`shouldDowngradeToPQ` without carrying any of that.
 */
const stampedForSuppressedExcursion = (
    state: any,
    offerType: OfferType
): { forceUseGenericCalcs: false } | { keepUpqForSuppressedExcursion: true } | undefined => {
    if (shouldPriceOnPqWithoutMovingEndpoint(state, offerType)) return { forceUseGenericCalcs: false };
    if (isOnUpqWithDealerOptOutBlocking(state, offerType)) return { keepUpqForSuppressedExcursion: true };
    return undefined;
};

const stampedForManualRebateEdit = (state: any, offerType: OfferType): { keepUpqForSuppressedExcursion: true } | undefined =>
    shouldPriceOnPqWithoutMovingEndpoint(state, offerType) || isOnUpqWithDealerOptOutBlocking(state, offerType)
        ? { keepUpqForSuppressedExcursion: true }
        : undefined;

const withMetaStamp = <T extends { meta?: any }>(action: T, stamp: Record<string, unknown> | undefined): T =>
    stamp === undefined ? action : { ...action, meta: { ...action.meta, ...stamp } };

const withPqPricing = <T extends { meta?: any }>(action: T, state: any, offerType: OfferType): T =>
    withMetaStamp(action, stampedForSuppressedExcursion(state, offerType));

const withUpqHoldForManualRebate = <T extends { meta?: any }>(action: T, state: any, offerType: OfferType): T =>
    withMetaStamp(action, stampedForManualRebateEdit(state, offerType));

export const restoreCalcEndpoint = (dispatch: any, state: any) => {
    if (!isRestorationEnabled(state)) return;
    const previousCalcEndpoint = getProgramsSlideOutPreviousCalcEndpoint(state);
    const previousOfferType = (getProgramsSlideOutPreviousOfferType(state) ?? getCurrentOfferType(state)) as OfferType;
    if (previousCalcEndpoint && previousCalcEndpoint !== getCalculationEndpointByOfferType(state, previousOfferType)) {
        dispatch(updateCurrentCalculationEndpoint(previousCalcEndpoint as CalculationEndpointType, previousOfferType));
    }
};

// Cash has no term, so the shared, cross-application getAppliedIncentiveIdsForSelectedTerm always
// returns an empty applied set for it (deliberate for other applications' flows - see
// programSelectors.getAppliedIncentiveIdsForCurrentOffer for the full explanation). Route through that
// cash-aware selector here too, instead of calling the term-based selector directly. See DE759607.
const getAppliedConditionalIdsForCurrentOffer = (state: any, conditionalIds: Set<number>): number[] =>
    programSelectors.getAppliedIncentiveIdsForCurrentOffer(state).filter((id: number) => conditionalIds.has(id));

const getTermConditionalIdSet = (state: any): Set<number> =>
    new Set(programSelectors.getConditionalIncentivesFromSelectedTerm(state).map((conditional) => conditional.incentiveId));

const keepsDealIncentives = (state: any, offerType: OfferType): boolean =>
    offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType);

const getDealIncentiveIds = (state: any, offerType: OfferType) =>
    paymentPayloadUtils.getDealIncentiveIdsForRequest(state, offerType);

const getConditionalIdsToCarryOnSwitch = (state: any, offerType: OfferType, standardIds: Set<number>): number[] =>
    (getDealIncentiveIds(state, offerType).conditional ?? []).filter((id) => !standardIds.has(id));

const getTermNonConditionalIdSet = (state: any): Set<number> =>
    new Set(
        programSelectors.getNonConditionalIncentivesFromSelectedTerm(state).map((nonConditional) => nonConditional.incentiveId)
    );

const getRecordedStandardIds = (state: any, offerType: OfferType, nonConditionalIdSet: Set<number>): number[] => {
    const source = hasRecordedNonConditionalSelection(state, offerType)
        ? getSelectedNonConditionalIdsFromPenciledInputs(state, offerType)
        : programSelectors.getAppliedIncentiveIdsForCurrentOffer(state, offerType);
    return source.filter((id: number) => nonConditionalIdSet.has(id));
};

const getProgramAppliedStandardIds = (program: ProgramQuote, conditionalIdSet: Set<number>): number[] | undefined => {
    const appliedIds = program.appliedIncentiveIds as number[] | undefined;
    if (appliedIds == null) return undefined;
    return appliedIds.filter((id) => !conditionalIdSet.has(id));
};

const getSwitchStandardIds = (
    state: any,
    offerType: OfferType,
    program: ProgramQuote,
    conditionalIdSet: Set<number>,
    termNonConditionals: ReturnType<typeof programSelectors.getNonConditionalIncentivesFromSelectedTerm>
): number[] | undefined => {
    const editedIds = incentiveSelectors.getEditedIncentiveIds(state, offerType);
    const termNonConditionalIdSet = new Set(termNonConditionals.map((incentive) => incentive.incentiveId));
    const dealerCashIdSet = new Set(
        termNonConditionals.filter(offerReduxUtils.incentives.isStoredDealerCash).map((incentive) => incentive.incentiveId)
    );
    const carriedDealerCashIds = getRecordedStandardIds(state, offerType, dealerCashIdSet).filter((id) => !editedIds.has(id));
    const incomingStandardIds = getProgramAppliedStandardIds(program, conditionalIdSet);
    const rowListsStandards = incomingStandardIds != null && incomingStandardIds.length > 0;
    const baseStandardIds = rowListsStandards
        ? incomingStandardIds
        : getRecordedStandardIds(state, offerType, termNonConditionalIdSet);
    const editsBase = (rowListsStandards ? incomingStandardIds : [...termNonConditionalIdSet]).some(
        (id) => editedIds.has(id) && !dealerCashIdSet.has(id)
    );
    if (!editsBase && carriedDealerCashIds.length === 0) return undefined;
    return [...new Set([...baseStandardIds, ...carriedDealerCashIds])].filter((id) => !editedIds.has(id));
};

const getStandardIdsToCarryOnSwitch = (
    program: ProgramQuote,
    carriedStandardIds: number[],
    conditionalIdSet: Set<number>
): number[] => {
    const incomingStandardIds = getProgramAppliedStandardIds(program, conditionalIdSet);
    if (incomingStandardIds == null || incomingStandardIds.length === 0) return carriedStandardIds;
    return carriedStandardIds.filter((id) => incomingStandardIds.includes(id));
};

/**
 * Whether the payment should omit the standards key so Payment Services seeds the program's own
 * standard rebates.
 *
 * True on a program switch where the caller supplied no standards, and on the deferred-standards
 * retry. That retry only runs because the supplied ids did not apply
 * (`isReapplyForDeferredStandardsPending`), so re-sending the same list cannot change the outcome —
 * and stating the standards key at all makes PO send `includeStandardRebates: false`, which stops PS
 * from seeding the incoming program's own standards. Handing the decision back to PS is the only
 * retry that can succeed. See DE763575.
 */
const shouldServiceSeedStandards = (
    isProgramSwitch: boolean,
    explicitNonConditionalIds: number[] | undefined,
    deferredStandardsReapplied: boolean,
    restorationEnabled: boolean
): boolean => (isProgramSwitch && explicitNonConditionalIds == null) || (restorationEnabled && deferredStandardsReapplied);

/**
 * Records the inputs that decide whether an apply states the standards key, so an intermittent
 * DE763575 recurrence is diagnosable from New Relic without a local reproduction.
 *
 * The suspicion is ordering: `applySelectedProgram` writes the standards selection into penciled
 * inputs *before* its payment fires, so a second apply that overlaps the first reads
 * `hasRecordedStandardsDecision: true` for a decision the app just wrote rather than one the user
 * made — and then states the key, which stops PS seeding the program's own standards. Logging the
 * decision inputs alongside the program context is what distinguishes that from a legitimate
 * same-program re-apply, which states the key by design.
 */
const logStandardsDecision = (attributes: Record<string, unknown>): void => {
    const message = 'MV:Programs - standards key decision on apply';
    console.debug(message, attributes);
    logNewRelicPageAction(message, attributes);
};

const canInferIncomingStandards = (appliedIds: number[] | undefined, knownConditionalIdSet: Set<number>): boolean =>
    knownConditionalIdSet.size > 0 && (appliedIds ?? []).length > 0;

const removeIncomingProgramStandardIds = (
    conditionalIds: number[],
    program: ProgramQuote,
    knownConditionalIdSet: Set<number>
): number[] => {
    if (!canInferIncomingStandards(program.appliedIncentiveIds as number[] | undefined, knownConditionalIdSet)) {
        return conditionalIds;
    }
    const incomingStandardIds = getProgramAppliedStandardIds(program, knownConditionalIdSet);
    if (incomingStandardIds == null || incomingStandardIds.length === 0) return conditionalIds;
    const incomingStandardIdSet = new Set(incomingStandardIds);
    return conditionalIds.filter((id) => !incomingStandardIdSet.has(id));
};

const getAvailableConditionalIdSet = (state: any): Set<number> =>
    new Set(programSelectors.getAvailableConditionalIncentives(state).map((incentive) => incentive.incentiveId));

const getDealCatalogConditionalIds = (state: any, offerType: OfferType): number[] => {
    const catalog = getIncentives(state)?.byOfferType?.[offerType]?.conditionals as Array<string | number> | undefined;
    if (!catalog) return [];
    return catalog.map((rawId) => offerReduxUtils.incentives.parseIncentiveId(String(rawId))).filter((id) => !Number.isNaN(id));
};

const recordSelectionWithoutPayment = (
    dispatch: any,
    offerType: OfferType,
    conditionalIds: number[],
    nonConditionalIds: number[],
    replaceNonConditionalSelection = false
) => {
    const selectionKeys = [
        ...offerReduxUtils.incentives.buildConditionalSelectionKeys(offerType, conditionalIds, true),
        ...offerReduxUtils.incentives.buildNonConditionalSelectionKeys(offerType, nonConditionalIds, true)
    ];
    dispatch(selectedConditionalIncentives(selectionKeys, true, false, replaceNonConditionalSelection));
};

const dispatchConditionalSelectionToPenciledInputs = (
    dispatch: any,
    state: any,
    incentive: any,
    selected: boolean,
    deferPayment: boolean
) => {
    const offerType = getCurrentOfferType(state);
    const conditionals = programSelectors.getConditionalIncentivesFromSelectedTerm(state);
    const conditionalIds = new Set(conditionals.map((conditional) => conditional.incentiveId));
    if (!conditionalIds.has(incentive.incentiveId)) return;

    const dealIds = keepsDealIncentives(state, offerType) ? getDealIncentiveIds(state, offerType) : undefined;
    const selectedIds = new Set(dealIds?.conditional ?? getAppliedConditionalIdsForCurrentOffer(state, conditionalIds));
    if (selected) {
        selectedIds.add(incentive.incentiveId);
    } else {
        selectedIds.delete(incentive.incentiveId);
    }

    const nonConditionalSelectedIds =
        dealIds?.nonConditional ?? getRecordedStandardIds(state, offerType, getTermNonConditionalIdSet(state));

    forceCalcEndpointToPqForIncentiveEdit(dispatch, state, offerType);

    if (deferPayment) {
        recordSelectionWithoutPayment(dispatch, offerType, [...selectedIds], nonConditionalSelectedIds);
        return;
    }

    // getSelectedProgram alone isn't reliable for cash: with restoration off, isAppliedRowDisplay's
    // ungated CASH short-circuit lets programs.selectedProgram get set even though cash has no lender
    // program to apply.
    const hasAppliedProgram = offerType !== CASH && Boolean(getSelectedProgram(state));
    const standardsSelectionRecorded = hasRecordedNonConditionalSelection(state, offerType);

    dispatch(
        withPqPricing(
            selectedConditionalIncentives(
                offerReduxUtils.incentives.buildConditionalSelectionKeys(offerType, [...selectedIds], true),
                true,
                !hasAppliedProgram
            ),
            state,
            offerType
        )
    );

    if (hasAppliedProgram) {
        dispatch(applyCurrentSelectedProgram([...selectedIds], nonConditionalSelectedIds, false, standardsSelectionRecorded));
    }
};

const dispatchNonConditionalSelectionToPenciledInputs = (
    dispatch: any,
    state: any,
    incentive: any,
    selected: boolean,
    deferPayment: boolean
) => {
    const offerType = getCurrentOfferType(state);
    const dealIds = keepsDealIncentives(state, offerType) ? getDealIncentiveIds(state, offerType) : undefined;
    const conditionalSelectedIds =
        dealIds?.conditional ?? getAppliedConditionalIdsForCurrentOffer(state, getTermConditionalIdSet(state));
    const nonConditionalSelectedIds = new Set(
        dealIds?.nonConditional ?? getRecordedStandardIds(state, offerType, getTermNonConditionalIdSet(state))
    );
    if (selected) nonConditionalSelectedIds.add(incentive.incentiveId);
    else nonConditionalSelectedIds.delete(incentive.incentiveId);

    forceCalcEndpointToPqForIncentiveEdit(dispatch, state, offerType);

    if (deferPayment) {
        // Scoped to cash (unlike the non-deferred dispatch below, this branch runs for every offer
        // type) - finance/lease's deferred conversion flow already relies on the reducer's
        // merge-forward here and has its own tested expectations, so only cash's toggle sends the
        // full non-conditional set as authoritative. See DE759607/US2076398.
        recordSelectionWithoutPayment(
            dispatch,
            offerType,
            conditionalSelectedIds,
            [...nonConditionalSelectedIds],
            (offerType === CASH && isCashFixesEnabled(state)) ||
                offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType)
        );
        return;
    }

    // getSelectedProgram alone isn't reliable for cash: with restoration off, isAppliedRowDisplay's
    // ungated CASH short-circuit lets programs.selectedProgram get set even though cash has no lender
    // program to apply.
    const hasAppliedProgram = offerType !== CASH && Boolean(getSelectedProgram(state));

    if (hasAppliedProgram) {
        dispatch(applyCurrentSelectedProgram(conditionalSelectedIds, [...nonConditionalSelectedIds], false, true));
        return;
    }

    if (offerType !== CASH || !isCashFixesEnabled(state)) return;

    // Cash has no lender program, so hasAppliedProgram is always false and the applyCurrentSelectedProgram
    // dispatch above never fires for it - without this, toggling a non-conditional (standard) cash
    // incentive's checkbox never wrote anything to state nor triggered a recalc, so the total/deal never
    // reflected the change. Dispatch both incentive families together (mirroring the combined-array
    // pattern already used by programsMiddleware.ts's hydrateStandardsSelectionFromTerm), same as the
    // conditional path above. nonConditionalSelectedIds here is always the full, authoritative
    // non-conditional set (including a just-removed id), so replaceNonConditionalSelection=true tells
    // the reducer to use it as-is instead of merge-forwarding the stale previously-applied set, or an
    // uncheck would be silently re-added. See DE759607/US2076398.
    dispatch(
        selectedConditionalIncentives(
            [
                ...offerReduxUtils.incentives.buildConditionalSelectionKeys(offerType, conditionalSelectedIds, true),
                ...offerReduxUtils.incentives.buildNonConditionalSelectionKeys(offerType, [...nonConditionalSelectedIds], true)
            ],
            true,
            true,
            true
        )
    );
};

const isNonConditionalIncentive = (state: any, incentiveId: number): boolean =>
    programSelectors
        .getNonConditionalIncentivesFromSelectedTerm(state)
        .some((nonConditional) => nonConditional.incentiveId === incentiveId);

const dispatchIncentiveSelectionToPenciledInputs = (
    dispatch: any,
    state: any,
    incentive: any,
    selected: boolean,
    deferPayment: boolean
) => {
    if (isNonConditionalIncentive(state, incentive.incentiveId)) {
        dispatchNonConditionalSelectionToPenciledInputs(dispatch, state, incentive, selected, deferPayment);
        return;
    }
    dispatchConditionalSelectionToPenciledInputs(dispatch, state, incentive, selected, deferPayment);
};

const detachEditedRowOnReselect = (
    dispatch: any,
    getState: () => any,
    incentive: any,
    selected: boolean,
    deferPayment: boolean
) => {
    const state = getState();
    const offerType = getCurrentOfferType(state);
    if (!selected || deferPayment || !offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType)) {
        return state;
    }
    const editedRows = (getManualIncentives(state, offerType) ?? []).filter(
        (row: any) => row.manual && Number(row.code) === Number(incentive.incentiveId)
    );
    if (editedRows.length === 0) {
        return state;
    }
    const customRows = editedRows.map((row: any) => {
        const { code: _code, pdId: _pdId, ...customRow } = row as Record<string, unknown>;
        return { ...customRow, isStandardIncentive: false };
    });
    editedRows.forEach((row: any) => dispatch(modifyManualIncentive(row, 'remove', false)));
    customRows.forEach((row: any) => dispatch(modifyManualIncentive(row, 'add', false)));
    return getState();
};

const toggleSelectedIncentiveThunk =
    (incentive: any, selected: boolean, onLegacyToggle?: (dispatch: any) => void, deferPayment = false) =>
    (dispatch: any, getState: any) => {
        const state = getState();
        if (!isRestorationEnabled(state)) {
            dispatch(toggleIncentiveCheckbox(incentive, selected));
            onLegacyToggle?.(dispatch);
            return;
        }
        dispatchIncentiveSelectionToPenciledInputs(
            dispatch,
            detachEditedRowOnReselect(dispatch, getState, incentive, selected, deferPayment),
            incentive,
            selected,
            deferPayment
        );
    };

export const toggleSelectedIncentive = (incentive: any, selected: boolean) => toggleSelectedIncentiveThunk(incentive, selected);

export const toggleSelectedIncentiveCompactUI = (incentive: any, selected: boolean, payment: boolean = false) =>
    toggleSelectedIncentiveThunk(incentive, selected, (dispatch) => dispatch(modifyProgramIncentive(incentive, selected, payment)));

export const deselectIncentiveForConversion = (incentive: any) => toggleSelectedIncentiveThunk(incentive, false, undefined, true);

export const modifyManualIncentiveCompactUI =
    (incentive: any, action: 'add' | 'remove' | 'edit', payment = false): DashStoreThunk =>
    (dispatch, getState) => {
        const state = getState();
        const offerType = getCurrentOfferType(state);
        if (payment) {
            forceCalcEndpointToPqForIncentiveEdit(dispatch, state, offerType);
        }
        const modifyAction = modifyManualIncentive(incentive, action, payment);
        const holdsUpqForManualRebate = payment && isRestorationEnabled(state);
        dispatch(holdsUpqForManualRebate ? withUpqHoldForManualRebate(modifyAction, state, offerType) : modifyAction);
    };

export const clear = () => {
    return { type: PROGRAMS_CLEAR_ALL };
};

export const clearProgramsForVehicleChange = () => {
    return { type: PROGRAMS_CLEAR_ALL };
};

/**
 * Opens the programs slideout and sets the navigation scope target to the current scenario.
 * This ensures that when the slideout renders, it reads/writes from the correct scenario's scoped store.
 */
export const openProgramsSlideOut = () => (dispatch: any, getState: any) => {
    const state = getState();
    // Get the current scope ID - this will be the scenario from which the user clicked "View Programs"
    const currentScopeId = scopedStateSelectors.getCurrentScopeId(state as any);

    // Set the navigation scope target so the programs activity knows which scenario's data to use
    dispatch(scopedActionsCreators.setNavigationScopeTarget(currentScopeId));

    const offerType = getCurrentOfferType(state);
    if (
        offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType) &&
        programSelectors.explicitLenderP1AppliesToOffer(state, offerType)
    ) {
        return;
    }

    dispatch({ type: OPEN_PROGRAMS_SLIDEOUT });
    forceCalcEndpointToPq(dispatch, state, offerType);
};

export const closeProgramsSlideOut = () => (dispatch: any, getState: any) => {
    const state = getState();
    if (!isProgramsSlideOutOpen(state)) return;
    restoreCalcEndpoint(dispatch, state);
    dispatch({ type: CLOSE_PROGRAMS_SLIDEOUT });
};

export const setCreditScoreOverride = (creditScoreOverride: number) => ({
    type: PROGRAMS_SET_CREDIT_SCORE_OVERRIDE,
    payload: creditScoreOverride
});

export const clearSelected = () => {
    return { type: PROGRAMS_CLEAR_SELECTED };
};

export const clearIncentives = () => {
    return { type: PROGRAMS_CLEAR_INCENTIVES };
};

export const setPendingApplyProgram = (program: PendingApplyProgram) => ({
    type: SET_PENDING_APPLY_PROGRAM,
    payload: program
});

export const clearPendingApplyProgram = () => ({
    type: CLEAR_PENDING_APPLY_PROGRAM
});

export const clearProgramLossIncentives = () => ({
    type: CLEAR_PROGRAM_LOSS_INCENTIVES
});

export const disableProgramLossIncentives = (incentiveIds: number[]) => ({
    type: DISABLE_PROGRAM_LOSS_INCENTIVES,
    payload: incentiveIds
});

export const pruneProgramLossIncentives = (availableIncentiveIds: number[]) => ({
    type: PRUNE_PROGRAM_LOSS_INCENTIVES,
    payload: availableIncentiveIds
});

export const markProgramAutoSelected = (offerType: OfferType, lenderProgram: ILenderProgram) => ({
    type: MARK_PROGRAM_AUTO_SELECTED,
    payload: { offerType, lenderProgram }
});

export const clearProgramAutoSelected = (offerType: OfferType) => ({
    type: CLEAR_PROGRAM_AUTO_SELECTED,
    payload: offerType
});

export const setAllTiersLoaded = (offerType: string, inputsKey?: string, isInputsReload = false) => ({
    type: SET_ALL_TIERS_LOADED,
    payload: offerType,
    meta: { inputsKey, isInputsReload }
});

export const allProgramsLoaded = () => ({
    type: ALL_PROGRAMS_LOADED
});

export const setRetainedProgramDisplay = (payload: {
    programName: string;
    lenderLegalName: string;
    offerType: string;
    program?: RetainedProgram;
}) => {
    return { type: SET_RETAINED_PROGRAM_DISPLAY, payload };
};

export const clearRetainedProgramDisplay = (offerType?: OfferType) => {
    return { type: CLEAR_RETAINED_PROGRAM_DISPLAY, ...(offerType && { payload: { offerType } }) };
};

export const setAppliedPaymentBaseline = (contextId: string, amount: number, rebates?: number) => {
    return { type: SET_APPLIED_PAYMENT_BASELINE, payload: { contextId, amount, rebates } };
};

export const clearAppliedPaymentBaseline = () => {
    return { type: CLEAR_APPLIED_PAYMENT_BASELINE };
};

export const getProgramQuotes =
    (options: GetAllQuotesOptions = {}): DashStoreThunk =>
    (dispatch, getState) => {
        const state = getState();
        const currentOfferType = getCurrentOfferType(state);
        const currentSelectedProgram = getSelectedTerm(state, currentOfferType);

        const hostSelectors = buildPaymentPayloadHostSelectors();

        const creditScoreOverride = 'creditScoreOverride' in options ? options.creditScoreOverride : undefined;
        const requestedTier = 'requestedTier' in options ? options.requestedTier : undefined;
        const primary = 'primary' in options ? options.primary : undefined;
        const keptLenderLookup = 'keptLenderLookup' in options ? options.keptLenderLookup : undefined;
        const inputsKey = programSelectors.getProgramQuotesInputsKey(state);

        const isNonPrimaryTierCall = requestedTier != null && primary !== true;
        const includeIncentives = !isNonPrimaryTierCall;

        const payload = paymentPayloadUtils.createPaymentPayload(state, hostSelectors, {
            type: REQUEST_PROGRAMS,
            meta: {
                isProgramCall: true,
                sourceApplication: ApplicationCodes.MV,
                ...options,
                includeIncentives
            }
        });

        const endpoint = getProgramOrchestratorUrl(state);

        dispatch({
            [RSAA]: {
                endpoint,
                method: 'POST',
                body: JSON.stringify(payload),
                headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: payload.traceId ?? '' },
                types: [
                    {
                        type: REQUEST_PROGRAMS,
                        meta: { creditScoreOverride }
                    },
                    {
                        type: REQUEST_PROGRAMS_SUCCESS,
                        meta: {
                            currentOfferType,
                            currentSelectedProgram,
                            creditScoreOverride,
                            requestedTier,
                            primary,
                            keptLenderLookup,
                            inputsKey,
                            enableProgramIncentivesRestoration: isRestorationEnabled(state),
                            isCashDealIncentivesFixesEnabled: isCashFixesEnabled(state),
                            ...buildIncentivesContextMeta(state, currentSelectedProgram)
                        }
                    },
                    { type: REQUEST_PROGRAMS_FAILURE, meta: { requestedTier, primary, keptLenderLookup, inputsKey } }
                ]
            }
        });
    };

const monthlyPaymentOf = (quote: ProgramQuote): number => {
    const amount = parseFloat(String(quote.monthlyPayment).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(amount) ? amount : Number.POSITIVE_INFINITY;
};

export const pickProgramForKeptLender = (
    quotes: ProgramQuote[],
    lender: KeptLender,
    termMonths: number | undefined
): ProgramQuote | undefined => {
    const candidates = quotes.flatMap((quote) =>
        quote.lenderProgram &&
        String(quote.lenderProgram.lenderId) === String(lender.lenderId) &&
        (termMonths == null || quote.term === termMonths)
            ? [{ quote, lenderProgram: quote.lenderProgram }]
            : []
    );
    const sameProgram = candidates.find(
        ({ lenderProgram }) =>
            lender.programName != null &&
            lenderProgram.programName === lender.programName &&
            lenderProgram.rateProgram === lender.rateProgram
    );
    if (sameProgram) return sameProgram.quote;
    return candidates.map(({ quote }) => quote).sort((a, b) => monthlyPaymentOf(a) - monthlyPaymentOf(b))[0];
};

export type ApplySelectedProgramOptions = {
    explicitConditionalIds?: number[];
    explicitNonConditionalIds?: number[];
    forceReapply?: boolean;
    deferredStandardsReapplied?: boolean;
    standardsSelectionEstablished?: boolean;
    manualProgramSelect?: boolean;
    reapplyCurrentProgram?: boolean;
    programsTableSelect?: boolean;
};

export const applySelectedProgram =
    (program: ProgramQuote, options: ApplySelectedProgramOptions = {}): DashStoreThunk =>
    (dispatch, getState) => {
        if (!program || !Object.keys(program).length) return;

        const {
            explicitConditionalIds,
            explicitNonConditionalIds,
            forceReapply = false,
            deferredStandardsReapplied = false,
            standardsSelectionEstablished = false,
            manualProgramSelect = false,
            reapplyCurrentProgram = false,
            programsTableSelect = false
        } = options;

        const state = getState();
        const offerType = getCurrentOfferType(state);

        const selectedProgram = getSelectedProgram(state);
        const programContextId = buildProgramContextId(program);
        const isLoadedForThisProgram = programSelectors.areIncentivesLoadedForProgram(state, offerType, program);
        const isProgramIncentivesRestorationEnabled = isRestorationEnabled(state);
        const isManualProgramSelect = manualProgramSelect && programSelectors.explicitLenderP1AppliesToOffer(state, offerType);
        const isCurrentProgramReapply = reapplyCurrentProgram && !manualProgramSelect && isProgramIncentivesRestorationEnabled;
        const keepGridOpen =
            isCurrentProgramReapply &&
            featureToggleSelectors.isConditionalRebateKeepGridOpenEnabled(state) &&
            !isFlexBuyPayment(state);
        const isSameProgramIncentiveReapply = reapplyCurrentProgram && !manualProgramSelect;
        const priceOnPqWithoutMovingEndpoint = !isManualProgramSelect && shouldPriceOnPqWithoutMovingEndpoint(state, offerType);

        const selectedTerm = getSelectedTerm(state, offerType);
        const term = program.term as number;
        const currTermId = getTermId(offerType, term, getDownPaymentByOfferType(state, offerType));
        const prevTermId = getTermIdForOfferType(state.offer as Offer, offerType);
        const prevIncentives = getSelectedIncentivesByOfferType(state, offerType);
        const shouldUseSelectedIncentives = isSelectedProgramContextMatch(selectedProgram, program);
        // conditional-only: the flattened selector cannot tell a recorded standard from a conditional,
        // which let standards leak into the conditional namespace across a program switch
        const penciledSelectedIncentives = isProgramIncentivesRestorationEnabled
            ? getSelectedConditionalIdsFromPenciledInputs(state, offerType)
            : programSelectors.getSelectedIncentives(state);
        const termConditionalIdSet = new Set(
            programSelectors.getConditionalIncentivesFromSelectedTerm(state).map((incentive) => incentive.incentiveId)
        );
        const priorTermAppliedIncentiveIds = isProgramIncentivesRestorationEnabled
            ? ((selectedTerm?.appliedIncentiveIds as number[] | undefined) ?? []).filter((id) => termConditionalIdSet.has(id))
            : [];
        const priorSelectedIncentives = isProgramIncentivesRestorationEnabled
            ? penciledSelectedIncentives.length > 0
                ? penciledSelectedIncentives
                : program.appliedIncentiveIds ?? []
            : state.programs?.selectedIncentives ?? program.appliedIncentiveIds ?? [];
        const rawConditionalIds =
            explicitConditionalIds != null
                ? explicitConditionalIds
                : shouldUseSelectedIncentives
                ? priorSelectedIncentives
                : isProgramIncentivesRestorationEnabled
                ? Array.from(
                      new Set([
                          ...(program.appliedIncentiveIds ?? []),
                          ...penciledSelectedIncentives,
                          ...priorTermAppliedIncentiveIds
                      ])
                  )
                : program.appliedIncentiveIds ?? [];
        const termNonConditionals = programSelectors.getNonConditionalIncentivesFromSelectedTerm(state);
        const termNonConditionalIdSet = new Set(termNonConditionals.map((incentive) => incentive.incentiveId));
        const availableConditionalIdSet = getAvailableConditionalIdSet(state);
        const knownConditionalIdSet = new Set([
            ...termConditionalIdSet,
            ...availableConditionalIdSet,
            ...getDealCatalogConditionalIds(state, offerType)
        ]);
        const selectedTermContextId = buildProgramContextIdFromTerm(selectedTerm);
        const isSameProgram =
            programPayloadUtil.isSelectedProgram(selectedTerm, program, offerType) &&
            Boolean(selectedTermContextId) &&
            selectedTermContextId === programContextId;
        const keepsDealConditionals = explicitConditionalIds != null && isSameProgram && keepsDealIncentives(state, offerType);
        let conditionalIds = rawConditionalIds;
        if (isProgramIncentivesRestorationEnabled) {
            conditionalIds = rawConditionalIds.filter((id: number) => !termNonConditionalIdSet.has(id));
            conditionalIds =
                isLoadedForThisProgram && !keepsDealConditionals
                    ? conditionalIds.filter((id: number) => availableConditionalIdSet.has(id))
                    : removeIncomingProgramStandardIds(conditionalIds, program, knownConditionalIdSet);
        }
        const isProgramSwitch = isProgramIncentivesRestorationEnabled && !isSameProgram;
        const resetsDefaultsOnSwitch =
            isProgramSwitch &&
            explicitConditionalIds == null &&
            explicitNonConditionalIds == null &&
            offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType);
        const switchStandardIds = resetsDefaultsOnSwitch
            ? getSwitchStandardIds(state, offerType, program, knownConditionalIdSet, termNonConditionals)
            : undefined;
        const requestedNonConditionalIds = explicitNonConditionalIds ?? switchStandardIds;
        const includeStandardIncentives = shouldServiceSeedStandards(
            isProgramSwitch,
            requestedNonConditionalIds,
            deferredStandardsReapplied,
            isProgramIncentivesRestorationEnabled
        );
        if (resetsDefaultsOnSwitch) conditionalIds = getConditionalIdsToCarryOnSwitch(state, offerType, termNonConditionalIdSet);
        const hasRecordedStandardsDecision = hasRecordedNonConditionalSelection(state, offerType);
        logStandardsDecision({
            includeStandardIncentives,
            isProgramSwitch,
            isSameProgram,
            hasRecordedStandardsDecision,
            explicitNonConditionalIdCount: requestedNonConditionalIds ? requestedNonConditionalIds.length : null,
            deferredStandardsReapplied,
            standardsSelectionEstablished,
            restorationEnabled: isProgramIncentivesRestorationEnabled,
            offerType,
            programContextId,
            selectedTermContextId,
            programTerm: program.term ?? null,
            programCreditTier: program.creditTier ?? null
        });
        const carriedStandardIds = getRecordedStandardIds(state, offerType, termNonConditionalIdSet);
        const standardIdsForSwitch = getStandardIdsToCarryOnSwitch(program, carriedStandardIds, knownConditionalIdSet);
        const selectedNonConditionalIds: number[] = isProgramIncentivesRestorationEnabled
            ? requestedNonConditionalIds ?? (isProgramSwitch ? standardIdsForSwitch : carriedStandardIds)
            : explicitNonConditionalIds ?? [...termNonConditionalIdSet];
        const honorNonConditionalSelection =
            isLoadedForThisProgram ||
            requestedNonConditionalIds != null ||
            (isProgramIncentivesRestorationEnabled && selectedNonConditionalIds.length > 0);
        const nonConditionalIncentiveIds: number[] = honorNonConditionalSelection ? selectedNonConditionalIds : [];

        const conditionalSelectionRecorded =
            resetsDefaultsOnSwitch || explicitConditionalIds != null || hasRecordedConditionalSelection(state, offerType);
        const conditionalIncentives = offerReduxUtils.incentives.buildConditionalSelectionKeys(
            offerType,
            conditionalIds,
            isProgramIncentivesRestorationEnabled && conditionalSelectionRecorded
        );
        const recordedNoneSelected =
            hasRecordedStandardsDecision && getSelectedNonConditionalIdsFromPenciledInputs(state, offerType).length === 0;
        const standardsSelectionRecorded =
            standardsSelectionEstablished ||
            switchStandardIds != null ||
            (isProgramSwitch ? recordedNoneSelected : hasRecordedStandardsDecision);
        const nonConditionalIncentives = includeStandardIncentives
            ? []
            : offerReduxUtils.incentives.buildNonConditionalSelectionKeys(
                  offerType,
                  nonConditionalIncentiveIds,
                  isProgramIncentivesRestorationEnabled && standardsSelectionRecorded
              );
        const currIncentives = [...conditionalIncentives, ...nonConditionalIncentives];

        const hasExplicitConditionalSelection = explicitConditionalIds != null;
        const isSameTerm = currTermId == prevTermId;
        const isSameIncentives = JSON.stringify(currIncentives) == JSON.stringify(prevIncentives);
        const tierScores = creditSelectors.getCreditTierAverageScores() as Record<string, number | undefined>;
        const requestedTierScore = program.requestedTier != null ? tierScores[program.requestedTier] : undefined;
        const creditScoreOverride = isProgramIncentivesRestorationEnabled
            ? requestedTierScore ?? state.programs?.creditScoreOverride ?? getManualCreditScore(state)
            : state.programs?.creditScoreOverride;
        const isSameCreditScoreOverride = state.programs?.appliedCreditScoreOverride === creditScoreOverride;

        const isRedundantApply =
            !forceReapply &&
            !hasExplicitConditionalSelection &&
            isSameTerm &&
            isSameIncentives &&
            isSameProgram &&
            isSameCreditScoreOverride;

        if (isRedundantApply) {
            if (state.programs?.pendingApplyProgram) {
                dispatch(clearPendingApplyProgram());
            }
            return;
        }

        if (isManualProgramSelect && !deferredStandardsReapplied) {
            resetProgramSuppliedOverrides({ dispatch, getState });
            dispatch(clearManualFieldOverrides([...PROGRAM_RESET_OVERRIDE_FIELDS, ...EXPLICIT_LENDER_CARRIED_OVERRIDE_FIELDS]));
        }

        const frozenLenderFeeOverrides = isSameProgramIncentiveReapply
            ? getFrozenLenderFeeOverrides(getState(), offerType)
            : undefined;

        if (isProgramIncentivesRestorationEnabled) {
            dispatch(
                setPendingApplyProgram(
                    buildPendingApplyProgram(program, offerType, nanoid(), {
                        explicitConditionalIds: resetsDefaultsOnSwitch ? undefined : conditionalIds,
                        explicitNonConditionalIds:
                            resetsDefaultsOnSwitch && switchStandardIds == null ? undefined : selectedNonConditionalIds,
                        deferredStandardsReapplied,
                        standardsSelectionEstablished: standardsSelectionRecorded && !includeStandardIncentives,
                        manualProgramSelect: isManualProgramSelect,
                        reapplyCurrentProgram: isCurrentProgramReapply
                    })
                )
            );
        }

        dispatch({
            type: 'APPLIED_PROGRAM',
            payload: { programData: program, incentives: currIncentives },
            meta: {
                keepManualIncentives: true,
                ...(creditScoreOverride != null && { creditScoreOverride }),
                sourceApplication: ApplicationCodes.MV,
                currentOfferType: offerType,
                ...(includeStandardIncentives && { includeStandardIncentives }),
                ...(resetsDefaultsOnSwitch && { carriesConditionalsOnSwitch: true }),
                ...(isManualProgramSelect && { manualProgramSelect: true }),
                ...(programsTableSelect && { programsTableSelect: true }),
                ...(isCurrentProgramReapply && { reapplyCurrentProgram: true }),
                ...(keepGridOpen && { keepGridOpen: true }),
                ...(frozenLenderFeeOverrides && {
                    [paymentPayloadUtils.FROZEN_LENDER_FEE_OVERRIDES_META]: frozenLenderFeeOverrides
                }),
                ...(priceOnPqWithoutMovingEndpoint && { forceUseGenericCalcs: false }),
                middleware: {
                    payment: true
                }
            }
        });

        if (creditScoreOverride != null && creditScoreOverride !== getManualCreditScore(state)) {
            dispatch({
                type: 'UPDATED_MANUAL_CREDIT_SCORE',
                payload: creditScoreOverride,
                meta: {}
            });
        }

        dispatch(clearSelected());
    };

type KeptLenderLookupResult = { error: true } | { error?: undefined; payments: IResponsePayment[] };

type KeptLenderLookup = { promise: Promise<KeptLenderLookupResult>; zip: string };

const keptLenderLookupSettlers = new Map<string, (result: KeptLenderLookupResult) => void>();
const keptLenderLookups = new WeakMap<object, KeptLenderLookup>();
let keptLenderLookupSeq = 0;

export const settleKeptLenderLookup = (action: AnyFSA): void => {
    const key = action.meta.keptLenderLookup;
    const settle = keptLenderLookupSettlers.get(key);
    if (!settle) return;

    keptLenderLookupSettlers.delete(key);
    settle(
        action.type === REQUEST_PROGRAMS_SUCCESS
            ? { payments: action.payload[action.meta.currentOfferType]?.payments ?? [] }
            : { error: true }
    );
};

const beginKeptLenderLookup = (dispatch: (action: any) => unknown, zip: string): KeptLenderLookup => {
    const key = String(++keptLenderLookupSeq);
    const promise = new Promise<KeptLenderLookupResult>((resolve) => keptLenderLookupSettlers.set(key, resolve));
    dispatch(getProgramQuotes({ keptLenderLookup: key }));
    return { promise, zip };
};

export const startKeptLenderLookup =
    (zipChange: AnyFSA): DashStoreThunk =>
    (dispatch, getState) => {
        keptLenderLookups.set(zipChange, beginKeptLenderLookup(dispatch, getShopperZip(getState())));
    };

const selectedProgramSignature = (state: any, offerType: OfferType): string =>
    `${getSelectedTermMonths(state, offerType)}|${getSelectedTerm(state, offerType)?.lenderProgram?.programName ?? ''}`;

export const reapplyKeptLenderProgram =
    (lender: KeptLender, zipChange: AnyFSA): DashStoreThunk<Promise<void>> =>
    async (dispatch, getState) => {
        const offerType = getCurrentOfferType(getState());
        const applyDealerOverridesInstead = () =>
            dispatch(
                applyDealerOverrides(
                    undefined,
                    false,
                    undefined,
                    isRestorationEnabled(getState()) ? { includeStandardIncentives: true } : {}
                )
            );
        if (!programSelectors.explicitLenderP1AppliesToOffer(getState(), offerType)) {
            applyDealerOverridesInstead();
            return;
        }

        const lookup = keptLenderLookups.get(zipChange) ?? beginKeptLenderLookup(dispatch, getShopperZip(getState()));
        keptLenderLookups.delete(zipChange);
        const signatureWhenStarted = selectedProgramSignature(getState(), offerType);

        const result = await lookup.promise;

        const state = getState();
        if (getCurrentOfferType(state) !== offerType) return;
        if (getShopperZip(state) !== lookup.zip) return;
        if (selectedProgramSignature(state, offerType) !== signatureWhenStarted) return;

        if (result.error) {
            applyDealerOverridesInstead();
            return;
        }

        const quotes = result.payments
            .map((payment) => mapPaymentResponseToProgramQuote(payment, true))
            .filter(isProgramNotExpired);
        const program = pickProgramForKeptLender(quotes, lender, getSelectedTermMonths(state, offerType));

        if (!program) {
            applyDealerOverridesInstead();
            return;
        }

        dispatch(applySelectedProgram(program, { manualProgramSelect: true, forceReapply: true }));
    };

export const applyCurrentSelectedProgram =
    (
        explicitConditionalIds?: number[],
        explicitNonConditionalIds?: number[],
        forceReapply = false,
        standardsSelectionEstablished = false
    ): DashStoreThunk =>
    (dispatch, getState) => {
        const state = getState();
        // Cash has no lender program to re-apply - "reload" just means re-fetching the
        // program-orchestrator's compatible incentives, same as the initial slide-out load.
        if (getCurrentOfferType(state) === CASH && isCashFixesEnabled(state)) {
            dispatch(getProgramQuotes());
            return;
        }
        const program = getSelectedProgram(state) ?? programSelectors.getProgramFromCurrentOffer(state);
        if (program?.lenderProgram?.programName) {
            dispatch(
                applySelectedProgram(program, {
                    explicitConditionalIds,
                    explicitNonConditionalIds,
                    forceReapply,
                    standardsSelectionEstablished,
                    reapplyCurrentProgram: true
                })
            );
        }
    };

export const setAvailableIncentivesFromPayment =
    (
        rawProgramIncentives: ProgramIncentives,
        offerType: OfferType,
        term?: number,
        compatibleIncentiveIds?: number[]
    ): DashStoreThunk =>
    (dispatch, getState) => {
        if (rawProgramIncentives == null) return;

        const state = getState();
        const selectedTerm = getSelectedTerm(state, offerType);
        const { programContextId, incentivesInputsKey } = buildIncentivesContextMeta(state, selectedTerm);
        const lenderId = selectedTerm?.lenderProgram?.lenderId;

        dispatch({
            type: PROGRAMS_INCENTIVES_REQUEST_SUCCESS,
            payload: { incentives: { [offerType]: rawProgramIncentives } },
            meta: {
                currentOfferType: offerType,
                term,
                ...(programContextId && { programContextId }),
                ...(lenderId != null && { lenderId }),
                ...(compatibleIncentiveIds != null && { compatibleIncentiveIds }),
                incentivesInputsKey
            }
        });
    };
